import { useState, useRef, useEffect } from 'react';
import VehicleCard from './VehicleCard';
import { getSubscriptionLimits, getSettings, getOwnedVehicleCount } from '../../utils/storage';
import { compressImageFile } from '../../utils/imageUtils';
import PhotoPickerModal from '../PhotoPickerModal';

export default function VehicleList({
  garage,
  vehicles,
  analyses,
  garages,
  onSelectVehicle,
  onAddVehicle,
  onBack,
  onUpdateGarage,
  onToast,
  onReferral,
  isDemoMode,
}) {
  const bannerInputRef = useRef(null);
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef(null);

  // Debounce search input by 300ms
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchTerm]);

  // Filter vehicles by debounced search term
  const filteredVehicles = debouncedSearch
    ? vehicles.filter((v) => {
        const term = debouncedSearch.toLowerCase();
        return (
          String(v.year).includes(term) ||
          v.make?.toLowerCase().includes(term) ||
          v.model?.toLowerCase().includes(term) ||
          v.internalNotes?.content?.toLowerCase().includes(term)
        );
      })
    : vehicles;

  async function handleBannerUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImageFile(file, 800, 0.8);
      onUpdateGarage(garage.id, { bannerPhoto: compressed });
    } catch {
      onToast?.('Failed to upload photo');
    }
    e.target.value = '';
  }

  if (!garage) return null;

  return (
    <div className="screen" style={{ paddingBottom: 100 }}>
      {/* Garage banner photo */}
      <input
        ref={bannerInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleBannerUpload}
      />
      <button
        onClick={() => setShowPhotoPicker(true)}
        style={{
          width: 'calc(100% + 40px)',
          marginLeft: -20,
          marginTop: -20,
          marginBottom: 16,
          height: garage.bannerPhoto ? 160 : 80,
          background: garage.bannerPhoto
            ? `url(${garage.bannerPhoto}) center/cover no-repeat`
            : 'var(--bg-elevated)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          cursor: 'pointer',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {!garage.bannerPhoto && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            Add Garage Photo
          </div>
        )}
        {garage.bannerPhoto && (
          <div style={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          </div>
        )}
      </button>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button
          onClick={onBack}
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: 'var(--bg-elevated)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div style={{ flex: 1 }}>
          <h1 className="screen-title" style={{ fontSize: '1.25rem' }}>{garage.name}</h1>
          <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
            {(() => {
              const limits = getSubscriptionLimits();
              const ownedCount = getOwnedVehicleCount();
              const plan = getSettings().subscription.plan;
              const max = limits.maxVehicles === Infinity ? '∞' : limits.maxVehicles;
              return `${ownedCount}/${max} vehicles (${plan === 'free' ? 'Free' : plan.charAt(0).toUpperCase() + plan.slice(1)} plan)`;
            })()}
          </p>
        </div>
        <button
          onClick={onAddVehicle}
          title="Add Vehicle"
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: 'var(--accent)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      </div>

      {/* Referral banner — show for non-demo, non-unlimited users */}
      {!isDemoMode && onReferral && (() => {
        const plan = getSettings().subscription.plan;
        if (plan === 'team') return null;
        return (
          <button
            onClick={onReferral}
            style={{
              width: '100%',
              padding: '14px 16px',
              background: 'var(--bg-elevated)',
              border: '1px solid rgba(211,71,74,0.3)',
              borderRadius: 'var(--radius-sm, 8px)',
              marginBottom: 16,
              cursor: 'pointer',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: 'rgba(211,71,74,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D3474A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <line x1="19" y1="8" x2="19" y2="14" />
                <line x1="22" y1="11" x2="16" y2="11" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                Refer a friend, unlock an extra car
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                Once your referral signs up, you can add an additional car to your garage.
              </div>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        );
      })()}

      {/* Search bar — only show when there are vehicles */}
      {vehicles.length > 0 && (
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            background: 'var(--bg-primary)',
            paddingBottom: 12,
            marginBottom: 4,
          }}
        >
          <div style={{ position: 'relative' }}>
            {/* Magnifying glass icon */}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--text-muted)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search by make, model, year, or notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 36px 10px 38px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                background: 'var(--bg-elevated)',
                color: 'var(--text-primary)',
                fontSize: '0.875rem',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            {/* Clear button */}
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: 'var(--bg-card)',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text-muted)',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                }}
              >
                ✕
              </button>
            )}
          </div>
          {/* Result count */}
          {debouncedSearch && (
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 8 }}>
              Showing {filteredVehicles.length} of {vehicles.length} vehicle{vehicles.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      )}

      {/* Vehicle list */}
      {vehicles.length === 0 && (
        <div
          className="card"
          style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: 'var(--text-muted)',
            marginBottom: 16,
          }}
        >
          <p style={{ fontSize: '0.9375rem', marginBottom: 4 }}>No vehicles yet.</p>
          <p style={{ fontSize: '0.8125rem' }}>Add your first vehicle to this garage.</p>
        </div>
      )}

      {/* Search empty state */}
      {vehicles.length > 0 && debouncedSearch && filteredVehicles.length === 0 && (
        <div
          className="card"
          style={{
            textAlign: 'center',
            padding: '30px 20px',
            color: 'var(--text-muted)',
            marginBottom: 16,
          }}
        >
          <p style={{ fontSize: '0.9375rem', marginBottom: 4 }}>No vehicles match your search.</p>
          <p style={{ fontSize: '0.8125rem' }}>Try a different search term.</p>
        </div>
      )}

      {filteredVehicles.map((vehicle) => (
        <VehicleCard
          key={vehicle.id}
          vehicle={vehicle}
          analysis={analyses[vehicle.id]}
          onClick={() => onSelectVehicle(vehicle.id)}
        />
      ))}

      <PhotoPickerModal
        show={showPhotoPicker}
        onClose={() => setShowPhotoPicker(false)}
        onSelect={(source) => {
          setShowPhotoPicker(false);
          bannerInputRef.current.removeAttribute('capture');
          bannerInputRef.current.click();
        }}
      />
    </div>
  );
}
